/**
 * A task's live status off the stores, on the one truth ladder every surface
 * reads (task-live-status: the live socket phase, then the story spine, then
 * the legacy lifecycle, else queued). The crown's receipt colours its dot
 * with it and the Dock's settle watch reads it; the classic inline card keeps
 * its own copy. Each store selector returns a primitive, so an app-wide
 * consumer re-renders only when this task's own reading changes, never on a
 * history list replacement or another task's message.
 */
import { useHistoryStore } from "@/stores/historyStore";
import { useTaskStore } from "@/stores/taskStore";
import { taskLiveStatus, type TaskLiveStatus } from "@/lib/task-live-status";

const SETTLED: ReadonlySet<TaskLiveStatus> = new Set<TaskLiveStatus>([
  "done",
  "stopped",
  "out_of_credits",
  "error",
]);

/** Done, stopped, out of credits or failed: the dot has come to rest. */
export function isSettled(status: TaskLiveStatus): boolean {
  return SETTLED.has(status);
}

/** The task's status while mounted; "queued" for no task (nothing is known). */
export function useTaskLiveStatus(taskId: string | null): TaskLiveStatus {
  const livePhase = useTaskStore((s) =>
    taskId ? s.tasks.get(taskId)?.phase : undefined
  );
  const liveError = useTaskStore((s) =>
    taskId ? Boolean(s.tasks.get(taskId)?.error) : false
  );
  const storyPhase = useHistoryStore((s) =>
    taskId
      ? (s.taskHistory.find((t) => t.id === taskId)?.storyPhase ?? null)
      : null
  );
  const lifecycleState = useHistoryStore((s) =>
    taskId
      ? (s.taskHistory.find((t) => t.id === taskId)?.lifecycleState ?? null)
      : null
  );
  return taskLiveStatus({ livePhase, liveError, storyPhase, lifecycleState });
}
